/**
 * Real-time event stream client.
 * Server: SSE at /api/events/stream?token=… (see backend/routes/events.py).
 * Usage:
 *   - `useLiveEvents(['inventory.lot.approved'], (evt) => refresh())`
 *   - `useConnectionStatus()` → 'open' | 'connecting' | 'offline' | 'idle'
 *
 * Design:
 * - Single shared EventSource across the app (module-level singleton) so one browser tab
 *   opens one connection regardless of how many pages/components subscribe.
 * - Auto-reconnect on error with exponential backoff (1s → 30s cap).
 * - Connection state broadcast to any subscriber (used by the header status badge).
 */
import { useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _es = null;
let _listeners = new Set();       // {types: string[]|null, cb: fn}
let _statusListeners = new Set(); // (status) => void
let _reconnectDelay = 1000;
let _reconnectTimer = null;
let _teardownTimer = null;
let _status = "idle"; // idle | connecting | open | offline
// iter257: false-offline debounce. SSE briefly drops for many benign reasons
// (proxy timeout, backend hot-reload, network hiccup). We used to declare
// "Bağlantı kesildi" on the very first onerror — spammy and misleading while
// REST API calls are still fine. New rule: on error we go to "connecting"
// first, and only flip to "offline" after ≥2 consecutive reconnect failures
// AND a definitive /api/health probe fails.
let _consecutiveErrors = 0;
const _OFFLINE_ERROR_THRESHOLD = 2;

async function _probeHealth() {
  try {
    const r = await fetch(`${API}/health`, { method: "GET", cache: "no-store" });
    return r.ok;
  } catch { return false; }
}

function _setStatus(s) {
  if (_status === s) return;
  _status = s;
  for (const cb of _statusListeners) {
    try { cb(s); } catch { /* ignore */ }
  }
}

export function getConnectionStatus() { return _status; }

function _ensureConnection() {
  if (typeof window === "undefined") return;
  if (_es && (_es.readyState === EventSource.OPEN || _es.readyState === EventSource.CONNECTING)) return;
  const token = localStorage.getItem("erp_token");
  if (!token) return;
  try {
    _setStatus("connecting");
    _es = new EventSource(`${API}/events/stream?token=${encodeURIComponent(token)}`);
    _es.onopen = () => {
      _reconnectDelay = 1000;
      _consecutiveErrors = 0; // iter257: reset debounce on successful open
      _setStatus("open");
    };
    _es.onmessage = (ev) => {
      let parsed;
      try { parsed = JSON.parse(ev.data); } catch { return; }
      for (const l of _listeners) {
        if (!l.types || l.types.length === 0 || l.types.includes(parsed.type)) {
          try { l.cb(parsed); } catch (e) { /* ignore individual handler errors */ }
        }
      }
    };
    _es.onerror = () => {
      try { _es?.close(); } catch {}
      _es = null;
      _consecutiveErrors += 1;
      // iter257: below-threshold errors show "connecting" (transient blip),
      // not "offline". Real disconnection is confirmed by a health probe
      // AFTER we've retried at least _OFFLINE_ERROR_THRESHOLD times.
      if (_consecutiveErrors < _OFFLINE_ERROR_THRESHOLD) {
        _setStatus("connecting");
      } else {
        _probeHealth().then((healthy) => {
          // If backend is reachable via REST, the app is NOT offline — SSE
          // just needs to reconnect (proxy hiccup, backend redeploy, etc.).
          _setStatus(healthy ? "connecting" : "offline");
        });
      }
      if (_reconnectTimer) clearTimeout(_reconnectTimer);
      _reconnectTimer = setTimeout(_ensureConnection, _reconnectDelay);
      _reconnectDelay = Math.min(_reconnectDelay * 2, 30000);
    };
  } catch (e) {
    _setStatus("offline");
  }
}

function _addListener(types, cb) {
  const entry = { types, cb };
  _listeners.add(entry);
  if (_teardownTimer) { clearTimeout(_teardownTimer); _teardownTimer = null; }
  _ensureConnection();
  return () => {
    _listeners.delete(entry);
    if (_listeners.size === 0 && _es) {
      if (_teardownTimer) clearTimeout(_teardownTimer);
      _teardownTimer = setTimeout(() => {
        if (_listeners.size === 0 && _es) {
          try { _es.close(); } catch {}
          _es = null;
          if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
          _setStatus("idle");
        }
        _teardownTimer = null;
      }, 1000);
    }
  };
}

/**
 * React hook: subscribe to backend events.
 * @param {string[]|null} types — filter (null = all events)
 * @param {(evt: {type, data, ts}) => void} onEvent
 */
export function useLiveEvents(types, onEvent) {
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;
  useEffect(() => {
    const unsubscribe = _addListener(types, (evt) => cbRef.current?.(evt));
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.isArray(types) ? types.join("|") : String(types)]);
}

/**
 * React hook: subscribe to connection status changes.
 * Also proactively opens the stream so the header badge appears immediately.
 */
export function useConnectionStatus() {
  const [status, setStatus] = useState(_status);
  useEffect(() => {
    _statusListeners.add(setStatus);
    // Trigger connection if not already open — using an all-events listener so it stays alive.
    const unsub = _addListener(null, () => {});
    setStatus(_status);
    return () => {
      _statusListeners.delete(setStatus);
      unsub();
    };
  }, []);
  return status;
}

/** Manual close — used on logout so a stale token isn't reused. */
export function closeEventStream() {
  if (_es) { try { _es.close(); } catch {} _es = null; }
  _listeners.clear();
  if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
  _reconnectDelay = 1000;
  _setStatus("idle");
}

// iter237 — Manual reconnect trigger for the header retry icon.
// Reuses the existing health-check + EventSource plumbing.  Debounced with
// a short lock so rapid double-clicks can't spam the server.  Never
// reloads the page or clears in-memory app state.
let _forceReconnectLock = false;
export const FORCE_RECONNECT_LOCK_MS = 2500;

export async function forceReconnect() {
  if (_forceReconnectLock) {
    return { ok: false, reason: "debounced" };
  }
  _forceReconnectLock = true;
  setTimeout(() => { _forceReconnectLock = false; }, FORCE_RECONNECT_LOCK_MS);

  _setStatus("connecting");
  // Cancel any pending backoff so we retry now.
  if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
  _reconnectDelay = 1000;
  // Ping backend health first (independent of SSE) so we return a
  // definitive verdict for the UI.
  let healthy = false;
  try {
    const r = await fetch(`${API}/health`, { method: "GET", cache: "no-store" });
    healthy = r.ok;
  } catch { healthy = false; }
  if (!healthy) {
    _setStatus("offline");
    return { ok: false, reason: "unreachable" };
  }
  // Rebuild the EventSource cleanly.
  if (_es) { try { _es.close(); } catch {} _es = null; }
  _ensureConnection();
  return { ok: true };
}
